import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { z } from 'zod';
import { queryKeys } from '../client/query-client';
/**
 * Progress writes (spec §6.3).
 *
 * Three rules, all the backend's and all load-bearing:
 *
 *  1. **A resume tick emits no event.** A position moves every few seconds of
 *     playback and auditing it would bury every other event under scrubbing
 *     noise — so it is cheap on their side, and we still throttle it because
 *     cheap is not free on a phone.
 *  2. **Completion is one-way.** `completed: false` after a finish is far more
 *     likely a stale player than a retraction, and un-finishing would silently
 *     revoke a badge. We never send it.
 *  3. **Badges are computed on read**, so a completion invalidates the journey
 *     rather than us adjusting a count locally.
 */
const Acknowledgement = z.object({ ok: z.boolean() }).passthrough();
const POSITION_THROTTLE_MS = 10_000;
/**
 * At most one write per interval per item. There is no trailing write: the
 * players send the final position with `force` on pause, unmount and tab hide.
 *
 * The memory has to outlive a render. It used to be a bare `new Map()` in the
 * hook body, so every `timeupdate` (about four a second) re-rendered the
 * player, got a fresh empty map and wrote — the member hit the API's 120 a
 * minute limit in half a minute and their progress stopped saving.
 */
export function createPositionThrottle(intervalMs = POSITION_THROTTLE_MS) {
    const lastSent = new Map();
    return (contentItemId, now, force = false) => {
        const previous = lastSent.get(contentItemId);
        if (!force && previous !== undefined && now - previous < intervalMs)
            return false;
        lastSent.set(contentItemId, now);
        return true;
    };
}
export function useRecordPosition(http) {
    const throttle = useRef(null);
    throttle.current ??= createPositionThrottle();
    return useMutation({
        mutationFn: async ({ contentItemId, positionSeconds, force, }) => {
            if (!throttle.current?.(contentItemId, Date.now(), force))
                return null;
            return http.post('/journey/progress', { contentItemId, positionSeconds }, {
                schema: Acknowledgement,
            });
        },
    });
}
export function useMarkComplete(http) {
    const queryClient = useQueryClient();
    return useMutation({
        // `completed: true` only. There is deliberately no way to send false.
        mutationFn: (contentItemId) => http.post('/journey/progress', { contentItemId, completed: true }, {
            schema: Acknowledgement,
        }),
        onSuccess: () => {
            // The journey, its badges and the streak are all computed on read, so the
            // only correct local action is to stop trusting the cached copy.
            void queryClient.invalidateQueries({ queryKey: queryKeys.journey.root });
        },
    });
}
export function useCompleteModule(http) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (moduleId) => http.post('/journey/modules/progress', { moduleId, completed: true }, {
            schema: Acknowledgement,
        }),
        onSuccess: () => {
            // A module completion can be the one that earns a badge.
            void queryClient.invalidateQueries({ queryKey: queryKeys.journey.root });
        },
    });
}
const ModuleCompletionUndone = z.object({ undone: z.boolean() }).passthrough();
/**
 * Take back a module the member marked done by mistake — the deliberate
 * counterpart of `useCompleteModule`, on its own route. Rule 2 above still
 * holds: the progress write never sends `completed: false`.
 */
export function useUndoModule(http) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (moduleId) => http.post(`/journey/modules/${moduleId}/progress/undo`, {}, {
            schema: ModuleCompletionUndone,
        }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.journey.root });
        },
    });
}
//# sourceMappingURL=progress.js.map